import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@repo/ui'
import * as Sentry from '@sentry/react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ArrowRight, Check, Download, Loader2, Trash, TriangleAlert, X } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { z } from 'zod'
import {
  ProposalSectionKind,
  ProposalSectionState,
  createSection,
  deleteProposal,
  deleteSection,
  deselectSectionVersion,
  exportProposal,
  getProposal,
  getProposalSources,
  renameSection,
  reorderSections,
  selectSectionVersion
} from '@/api/proposals/proposals'
import { moveSection } from '@/components/proposal/moveSection'
import { formatRefineUserMessage } from '@/components/proposal/refineMessage'
import { mergeSectionTranscript } from '@/common/proposalStream'
import type { ProposalDraftingProgressSection } from '@/common/proposalStream'
import { useProposalStream } from '@/common/useProposalStream'
import { useSectionMessages } from '@/common/useSectionMessages'
import { ProposalSectionNav } from '@/components/proposal/ProposalSectionNav'
import { ProposalSectionChat } from '@/components/proposal/ProposalSectionChat'
import { PaneResizer } from '@/components/PaneResizer'
import { EditableProposalTitle } from '@/components/proposal/EditableProposalTitle'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { PageHeader } from '@/components/PageHeader'
import { ProposalSourcesPanel } from '@/components/proposal/ProposalSourcesPanel'

const searchSchema = z.object({
  autobuild: z.boolean().optional()
})

const SECTION_NAV_WIDTH_KEY = 'proposal:sectionNavWidth'
const SECTION_NAV_MIN_WIDTH = 240
const SECTION_NAV_MAX_WIDTH = 520
const SECTION_NAV_DEFAULT_WIDTH = 300
const DEFAULT_DRAFTING_MESSAGE = 'Parsing the template and preparing sections.'
const SOURCE_CONTENT_REQUIRED_MESSAGE = 'Add source content before building'

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/proposal-agent/$proposalId/')({
  validateSearch: searchSchema,
  loader: async ({ params }) => {
    const projectId = parseInt(params.projectId, 10)
    const proposalId = parseInt(params.proposalId, 10)
    const proposal = await getProposal(projectId, proposalId)
    return { proposal }
  },
  head: () => ({ meta: [{ title: 'Proposal Agent - Snowvault' }] }),
  component: ProposalWorkspace
})

function ProposalWorkspace() {
  const { orgSlug, projectId, proposalId } = Route.useParams()
  const { autobuild } = Route.useSearch()
  const { proposal } = Route.useLoaderData()
  const { userOrgProfile } = Route.useRouteContext().user
  const router = useRouter()
  const navigate = Route.useNavigate()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const projectIdNum = parseInt(projectId, 10)
  const proposalIdNum = parseInt(proposalId, 10)

  const sections = proposal.sections
  // Local mirror of section order so a drag reorder shows immediately, before the server round-trip
  // and loader invalidation reconcile it.
  const [orderedSections, setOrderedSections] = useState(sections)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOrderedSections(sections)
  }, [sections])

  // User-resizable section nav. Default is roomy enough that typical headings
  // read in full; the chosen width is clamped and persisted across sessions.
  const [sectionNavWidth, setSectionNavWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem(SECTION_NAV_WIDTH_KEY))
    return Number.isFinite(saved) && saved >= SECTION_NAV_MIN_WIDTH && saved <= SECTION_NAV_MAX_WIDTH
      ? saved
      : SECTION_NAV_DEFAULT_WIDTH
  })
  const sectionNavWidthRef = useRef(sectionNavWidth)
  const handleSectionNavResize = useCallback((delta: number) => {
    const next = Math.min(SECTION_NAV_MAX_WIDTH, Math.max(SECTION_NAV_MIN_WIDTH, sectionNavWidthRef.current + delta))
    sectionNavWidthRef.current = next
    setSectionNavWidth(next)
  }, [])
  const handleSectionNavResizeCommit = useCallback(() => {
    window.localStorage.setItem(SECTION_NAV_WIDTH_KEY, String(Math.round(sectionNavWidthRef.current)))
  }, [])

  // User-chosen section; null means "follow the stream" or fall back to first.
  const [manualSectionId, setManualSectionId] = useState<number | null>(null)

  const [actionError, setActionError] = useState<string | null>(null)
  const [sourceCount, setSourceCount] = useState<number | null>(null)
  const [sourcesWarning, setSourcesWarning] = useState<string | null>(null)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [exportWarnings, setExportWarnings] = useState<Array<string>>([])
  const [exporting, setExporting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [autoEditSectionId, setAutoEditSectionId] = useState<number | null>(null)

  // Use a stable ref for the refetch callback so useProposalStream can call it without a
  // circular dependency between stream and messages.
  const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve())
  // clearBuffersRef is hoisted the same way so onClose can call clearSectionBuffers without
  // referencing stream before it is defined.
  const clearBuffersRef = useRef<(id: number) => void>(() => {})
  const stream = useProposalStream({
    router,
    onClose: (closedSectionId) => {
      void refetchRef.current().then(() => {
        if (closedSectionId !== null) clearBuffersRef.current(closedSectionId)
      })
    }
  })
  const clearStreamError = stream.clearError
  const sourceContentRequired = stream.error === SOURCE_CONTENT_REQUIRED_MESSAGE
  const showSourceContentWarning = sourceContentRequired || (sourceCount === 0 && stream.error === null)
  const visibleActionError = sourceContentRequired ? actionError : (stream.error ?? actionError)

  // Derive the active section: manual pick wins; fall back to stream-follow, then first.
  const selectedSectionId = manualSectionId ?? stream.activeSectionId ?? sections[0]?.sectionId ?? null

  const messages = useSectionMessages({
    projectId: projectIdNum,
    proposalId: proposalIdNum,
    sectionId: selectedSectionId
  })
  // Intentionally no dependency array: runs every render to keep the refs pointing at the latest closures.
  useLayoutEffect(() => {
    refetchRef.current = messages.refetch
    clearBuffersRef.current = stream.clearSectionBuffers
  })

  const allReady = sections.length > 0 && sections.every((s) => s.state === ProposalSectionState.Ready)
  const presentationAgentEnabled = userOrgProfile?.enabled_features.presentation_agent ?? false
  const buildEndpoint = `/api/projects/${projectIdNum}/proposals/${proposalIdNum}/build`
  const buildDisabled = sourceCount === null || stream.isStreaming

  const handleBuild = useCallback(() => {
    setActionError(null)
    setSourcesWarning(null)
    if (sourceCount === null) {
      return
    }
    stream.start(buildEndpoint, { modelTemp: 0.5 })
  }, [stream, buildEndpoint, sourceCount])

  useEffect(() => {
    let cancelled = false
    void getProposalSources(projectIdNum, proposalIdNum)
      .then((sources) => {
        if (!cancelled) setSourceCount(sources.length)
      })
      .catch(() => {
        if (!cancelled) setSourceCount(0)
      })
    return () => {
      cancelled = true
    }
  }, [projectIdNum, proposalIdNum])

  useEffect(() => {
    if (sourceCount !== null && sourceCount > 0 && sourceContentRequired) {
      clearStreamError()
    }
  }, [clearStreamError, sourceCount, sourceContentRequired])

  useEffect(() => {
    if (!stream.statusMessage?.includes('exceeded the model context')) return
    const timer = window.setTimeout(() => setSourcesWarning(stream.statusMessage), 0)
    return () => window.clearTimeout(timer)
  }, [stream.statusMessage])

  // Auto-start the first build when the create flow hands us the autobuild intent. Consume the
  // flag immediately so a refresh never silently re-runs an expensive build.
  const autoBuildTriggered = useRef(false)
  useEffect(() => {
    if (!autobuild || autoBuildTriggered.current || sourceCount === null) return
    autoBuildTriggered.current = true
    void navigate({ search: {}, replace: true })
    handleBuild()
  }, [autobuild, handleBuild, navigate, sourceCount])

  function handleRefine(sectionId: number, instruction: string, excerpt?: string) {
    setActionError(null)
    stream.start(
      `/api/projects/${projectIdNum}/proposals/${proposalIdNum}/sections/${sectionId}/refine`,
      { instruction, excerpt: excerpt ?? '', modelTemp: 0.5 },
      { sectionId, userText: formatRefineUserMessage(instruction, excerpt) }
    )
  }

  function handleRegenerate(sectionId: number) {
    setActionError(null)
    setSourcesWarning(null)
    stream.start(`/api/projects/${projectIdNum}/proposals/${proposalIdNum}/sections/${sectionId}/regenerate`, {
      modelTemp: 0.5
    })
  }

  async function handleSelect(sectionId: number, messageId: number) {
    setActionError(null)
    try {
      await selectSectionVersion(projectIdNum, proposalIdNum, sectionId, messageId)
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum, sectionId } })
      setActionError(err instanceof Error ? err.message : 'Could not select version')
    }
  }

  async function handleDeselect(sectionId: number) {
    setActionError(null)
    try {
      await deselectSectionVersion(projectIdNum, proposalIdNum, sectionId)
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum, sectionId } })
      setActionError(err instanceof Error ? err.message : 'Could not unselect version')
    }
  }

  async function handleExport() {
    setActionError(null)
    setExportWarnings([])
    setExporting(true)
    try {
      const result = await exportProposal(projectIdNum, proposalIdNum)
      const a = document.createElement('a')
      a.href = result.downloadUrl
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      if (result.warnings.length > 0) {
        setExportWarnings(result.warnings.map((warning) => warning.message))
      }
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum } })
      setActionError(err instanceof Error ? err.message : 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  async function handleDelete() {
    setActionError(null)
    try {
      await deleteProposal(projectIdNum, proposalIdNum)
      await router.navigate({ to: '/$orgSlug/$projectId/proposal-agent', params: { orgSlug, projectId } })
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum } })
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    }
  }

  async function handleRenameSection(sectionId: number, heading: string) {
    try {
      await renameSection(projectIdNum, proposalIdNum, sectionId, heading)
      await router.invalidate()
      setAutoEditSectionId(null)
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum, sectionId } })
      throw err
    }
  }

  async function handleDeleteSection(sectionId: number) {
    setActionError(null)
    try {
      await deleteSection(projectIdNum, proposalIdNum, sectionId)
      if (manualSectionId === sectionId) setManualSectionId(null)
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum, sectionId } })
      setActionError(err instanceof Error ? err.message : 'Could not delete section')
      throw err
    }
  }

  async function handleReorder(activeId: number, overId: number) {
    setActionError(null)
    const next = moveSection(orderedSections, activeId, overId)
    if (next === orderedSections) return
    setOrderedSections(next)
    try {
      await reorderSections(
        projectIdNum,
        proposalIdNum,
        next.map((s) => s.sectionId)
      )
      await router.invalidate()
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum } })
      setActionError(err instanceof Error ? err.message : 'Could not reorder sections')
      await router.invalidate()
      setOrderedSections(sections)
    }
  }

  async function handleAddSection() {
    setActionError(null)
    try {
      const created = await createSection(projectIdNum, proposalIdNum, 'New section')
      await router.invalidate()
      setManualSectionId(created.sectionId)
      setAutoEditSectionId(created.sectionId)
    } catch (err) {
      Sentry.captureException(err, { extra: { projectId: projectIdNum, proposalId: proposalIdNum } })
      setActionError(err instanceof Error ? err.message : 'Could not add section')
    }
  }

  const selectedSection = sections.find((s) => s.sectionId === selectedSectionId) ?? null

  const draftingSection =
    stream.isStreaming && stream.activeSectionId !== null
      ? sections.find((s) => s.sectionId === stream.activeSectionId)
      : undefined
  const showDraftingPill = !!draftingSection && draftingSection.sectionId !== selectedSectionId

  const optimistic = selectedSectionId !== null ? (stream.optimisticBySectionId[selectedSectionId] ?? null) : null
  const committed = selectedSectionId !== null ? (stream.committedBySectionId[selectedSectionId] ?? null) : null
  const transcript = mergeSectionTranscript(messages.messages, optimistic, committed)

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: projectTitle, to: projectTo },
          { label: 'Proposal Agent', to: `/${orgSlug}/${projectId}/proposal-agent` },
          { label: proposal.documentName }
        ]}
        actions={
          <div className="flex items-center gap-2">
            {allReady && (
              <span className="flex items-center gap-1 text-sm font-medium text-primary">
                <Check className="size-4" /> Ready for export
              </span>
            )}
            {allReady && presentationAgentEnabled && !stream.isStreaming && (
              <Button
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: '/$orgSlug/$projectId/presentation-agent',
                    params: { orgSlug, projectId },
                    search: { fromProposalId: proposalIdNum }
                  })
                }
              >
                Create presentation
              </Button>
            )}
            <Button onClick={handleExport} disabled={!allReady || exporting || stream.isStreaming}>
              {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              {exporting ? 'Exporting...' : 'Export to Doc'}
              <ArrowRight className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label="Delete proposal" onClick={() => setConfirmDelete(true)}>
              <Trash className="size-4" />
            </Button>
          </div>
        }
      />

      <AlertDialog
        open={confirmDelete}
        onOpenChange={(o) => {
          if (!o) setConfirmDelete(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{proposal.documentName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this proposal and all of its sections. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                setConfirmDelete(false)
                void handleDelete()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Proposal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-6">
        <div className="flex items-center justify-between">
          <EditableProposalTitle
            projectId={projectIdNum}
            proposalId={proposalIdNum}
            documentName={proposal.documentName}
            onRenamed={() => router.invalidate()}
          />
          <ProposalSourcesPanel
            projectId={projectIdNum}
            proposalId={proposalIdNum}
            templateDocId={proposal.templateDocId}
            onChange={setSourceCount}
            open={sourcesOpen}
            onOpenChange={setSourcesOpen}
          />
        </div>

        {showSourceContentWarning && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
          >
            <TriangleAlert aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
              <p className="font-medium">Source content needed</p>
              <p className="mt-1 text-sm text-amber-900 dark:text-amber-200">
                Choose at least one source before building your proposal.
              </p>
              <Button type="button" size="sm" className="mt-3" onClick={() => setSourcesOpen(true)}>
                Choose source content
              </Button>
            </div>
          </div>
        )}

        {visibleActionError && <p className="text-sm text-destructive">{visibleActionError}</p>}

        {exportWarnings.length > 0 && (
          <div className="flex items-start justify-between gap-3 rounded border bg-muted p-3 text-sm text-muted-foreground">
            <div className="space-y-1">
              <p className="font-medium text-foreground">Export completed with template warnings.</p>
              {exportWarnings.slice(0, 3).map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
            <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={() => setExportWarnings([])}>
              <X className="size-4" />
            </Button>
          </div>
        )}

        {sourcesWarning && (
          <div className="flex items-start justify-between gap-3 rounded border bg-muted p-3 text-sm text-muted-foreground">
            <p>{sourcesWarning}</p>
            <Button variant="ghost" size="icon" className="size-7 shrink-0" onClick={() => setSourcesWarning(null)}>
              <X className="size-4" />
            </Button>
          </div>
        )}

        {sections.length === 0 && (stream.isStreaming || autobuild) ? (
          <ProposalDraftingProgress
            progressSections={stream.progressSections}
            activeSectionId={stream.activeSectionId}
            statusMessage={stream.statusMessage}
          />
        ) : (
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <aside style={{ width: sectionNavWidth }} className="min-h-0 shrink-0 overflow-y-auto pr-3">
              <ProposalSectionNav
                sections={orderedSections}
                activeSectionId={selectedSectionId}
                autoEditSectionId={autoEditSectionId}
                onSelectSection={setManualSectionId}
                onRenameSection={handleRenameSection}
                onDeleteSection={handleDeleteSection}
                onReorder={handleReorder}
                onAddSection={handleAddSection}
              />
            </aside>
            <PaneResizer
              value={sectionNavWidth}
              min={SECTION_NAV_MIN_WIDTH}
              max={SECTION_NAV_MAX_WIDTH}
              onDelta={handleSectionNavResize}
              onCommit={handleSectionNavResizeCommit}
              ariaLabel="Resize the sections panel"
            />
            <section className="min-h-0 min-w-0 flex-1 overflow-hidden pl-3">
              {selectedSection &&
              selectedSection.state === ProposalSectionState.Pending &&
              selectedSection.kind !== ProposalSectionKind.Manual &&
              !stream.isStreaming ? (
                <div className="flex flex-col items-center justify-center gap-3 pt-16 text-center">
                  <p className="text-sm text-muted-foreground">This section hasn&apos;t been drafted yet.</p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant="outline" size="sm" onClick={handleBuild} disabled={buildDisabled}>
                          Resume drafting
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {sourceCount === null && <TooltipContent>Loading source context</TooltipContent>}
                  </Tooltip>
                </div>
              ) : selectedSection ? (
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  {draftingSection && showDraftingPill && (
                    <button
                      type="button"
                      onClick={() => setManualSectionId(draftingSection.sectionId)}
                      className="mb-3 inline-flex items-center gap-2 self-start rounded-full border bg-muted px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/70"
                    >
                      <Loader2 className="size-3 animate-spin" />
                      Drafting {draftingSection.heading} -&gt; jump
                    </button>
                  )}
                  <ProposalSectionChat
                    projectId={projectIdNum}
                    proposalId={proposalIdNum}
                    section={selectedSection}
                    messages={transcript}
                    messagesLoading={messages.loading}
                    messagesError={messages.error}
                    streamingText={stream.streamingBySectionId[selectedSection.sectionId]}
                    isActive={stream.activeSectionId === selectedSection.sectionId}
                    isStreaming={stream.isStreaming}
                    statusMessage={stream.statusMessage}
                    onRefine={handleRefine}
                    onRegenerate={handleRegenerate}
                    regenerateDisabled={buildDisabled}
                    onStop={stream.stop}
                    onSelect={handleSelect}
                    onDeselect={handleDeselect}
                    onEdited={() => messages.refetch()}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select a section to review.</p>
              )}
            </section>
          </div>
        )}
      </div>
    </>
  )
}

function ProposalDraftingProgress({
  progressSections,
  activeSectionId,
  statusMessage
}: {
  progressSections: Array<ProposalDraftingProgressSection>
  activeSectionId: number | null
  statusMessage: string | null
}) {
  const activeSection =
    activeSectionId !== null ? progressSections.find((section) => section.sectionId === activeSectionId) : undefined
  const completedSections = progressSections.filter((section) => section.sectionId !== activeSectionId)
  const activeMessage = activeSection
    ? `Generating ${activeSection.heading}`
    : (statusMessage ?? DEFAULT_DRAFTING_MESSAGE)

  return (
    <div className="flex flex-1 items-center justify-center px-6 text-center">
      <div className="w-full max-w-sm space-y-4 text-left">
        <p className="text-sm font-medium">Drafting your proposal...</p>
        <ol className="space-y-3 text-left">
          {completedSections.map((section) => (
            <li
              key={section.sectionId}
              className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-3 text-sm"
            >
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-3.5" />
              </span>
              <span className="min-w-0 truncate text-foreground">{section.heading}</span>
            </li>
          ))}
          <li className="grid min-w-0 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="size-5 shrink-0 animate-spin" />
            <span className="min-w-0 truncate">{activeMessage}</span>
          </li>
        </ol>
      </div>
    </div>
  )
}
